import React, { useState, useEffect } from "react";
import { useAppContext } from "../context/AppContext";
import { supabase } from "../services/supabase";
import NoteIcon from "./NoteIcon";
import ORCLogo from "../assets/ORClogo.png";

const Navbar = () => {
  const flyoutRef = React.useRef(null);
  const hamburgerRef = React.useRef(null);
  const { mode, setMode, darkMode, setDarkMode, setCurrentTab } = useAppContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event) {
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  // Load session on mount and subscribe to auth changes
  useEffect(() => {
    const fetchSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.user?.email) {
        setUserEmail(data.session.user.email);
      }
    };

    fetchSession();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserEmail(session?.user?.email || null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload(); // or navigate("/login") if using routing
  };

  const handleChangePassword = async () => {
    if (!userEmail) {
      alert("No user logged in. Please log in first.");
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
      redirectTo: `${window.location.origin}/reset-password`, // must be whitelisted in Supabase
    });

    if (error) {
      console.error("Password reset error:", error.message);
      alert("Failed to send password reset email: " + error.message);
    } else {
      alert("Password reset email sent! Check your inbox.");
    }
  };

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-2 bg-white text-gray-900 dark:bg-gray-900 dark:text-white shadow-md"
      >
        {/* Logo + Title */}
        <button
          onClick={() => setCurrentTab('Home')}
          className="flex items-center gap-2 font-bold text-lg hover:opacity-80 transition-opacity cursor-pointer"
        >
          <img
            src={ORCLogo}
            alt="Outdoorec Logo"
            width={120}
            height={37}
            style={{ maxWidth: '120px', maxHeight: '37px', width: '120px', height: '37px' }}
            className="object-contain"
          />
          <span style={{ fontFamily: 'Times New Roman, Times, serif', fontSize: '1.5em' }}>Dashboard</span>
        </button>

        {/* Desktop Controls: only visible md and up */}
        <div className="hidden md:flex items-center gap-4 relative">
    {/*
    <button className={`px-3 py-1 rounded font-medium transition-colors flex items-center gap-2 bg-blue-500 text-white`} onClick={() => setMode(mode === "beginner" ? "advanced" : "beginner")}>{mode === "beginner" ? "Beginner" : "Advanced"}</button>
    */}
  <button className="px-3 py-1 rounded font-medium transition-colors flex items-center gap-2 bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 shadow" onClick={() => setMode(mode === "beginner" ? "advanced" : "beginner")}> <NoteIcon /> Field Notes</button>
          <button className="px-2 py-1 rounded bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 shadow transition-colors" onClick={() => setDarkMode(!darkMode)}>{darkMode ? "🌙" : "☀️"}</button>
          <div className="relative">
            <button onClick={() => setMenuOpen(!menuOpen)} className="w-8 h-8 rounded-full bg-gray-300 text-gray-900 dark:bg-gray-600 dark:text-white flex items-center justify-center"><span role="img" aria-label="profile">👤</span></button>
            {/* Only show dropdown on desktop */}
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                {userEmail === "benford_bennett@hotmail.com" && (<button onMouseDown={e => { setMenuOpen(false); if (typeof window !== 'undefined') window.scrollTo(0, 0); setCurrentTab('Admin'); }} className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg">Admin Page</button>)}
                <button onMouseDown={handleChangePassword} className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">Change Password</button>
                <button onMouseDown={handleLogout} className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg">Logout</button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Hamburger: only visible below md */}
        <div className="flex md:hidden items-center relative">
          <button ref={hamburgerRef} className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 z-50" onClick={() => setMenuOpen(!menuOpen)} aria-label="Open menu">
            <svg className="w-7 h-7 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          {menuOpen && (
            <div ref={flyoutRef} className="absolute top-12 right-0 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-40 flex flex-col gap-2 p-4">
              {/*
              <button className={`px-3 py-1 rounded font-medium transition-colors flex items-center gap-2 bg-blue-500 text-white`} onClick={() => { setMode(mode === "beginner" ? "advanced" : "beginner"); setMenuOpen(false); }}>{mode === "beginner" ? "Beginner" : "Advanced"}</button>
              */}
              <button className="px-3 py-1 rounded font-medium transition-colors flex items-center gap-2 bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 shadow" onClick={() => { setMode(mode === "beginner" ? "advanced" : "beginner"); setMenuOpen(false); }}><NoteIcon /> Field Notes</button>
              <button className="px-2 py-1 rounded bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 shadow transition-colors" onClick={() => { setDarkMode(!darkMode); setMenuOpen(false); }}>{darkMode ? "🌙" : "☀️"}</button>
              {userEmail === "benford_bennett@hotmail.com" && (<button onMouseDown={e => { setMenuOpen(false); if (typeof window !== 'undefined') window.scrollTo(0, 0); setCurrentTab('Admin'); }} className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg">Admin Page</button>)}
              <button onMouseDown={() => { handleChangePassword(); setMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">Change Password</button>
              <button onMouseDown={() => { handleLogout(); setMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg">Logout</button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

export default Navbar;
